import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["menuItem"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
/* eslint-disable no-use-before-define */
import React, { useState } from 'react';
import { Tab as SemanticUiTab } from 'semantic-ui-react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isString from 'lodash/isString';
import { TabsContextProvider } from './Tabs.context';
import { getGridProps, getMenuProps } from './Tabs.utils';
import Content from './components/Content';
import Item from './components/Item';
import MenuItem from './components/MenuItem';
import { findComponentsByType } from '../../../../services/components';
import { ChildrenPropType, StylePropType } from '../../../../services/proptypes';
import { classNames } from '../../../../utils/classname';
import useEventPropagation from '../../hooks/use-event-propagation/use-event-propagation';
import { createComponent } from '../../services/components';
import './Tabs.css';
var defaultProps = {
  activeIndex: undefined,
  defaultActiveIndex: null,
  deselectable: false,
  children: {},
  panes: [],
  vertical: false,
  menuProps: {},
  grid: {
    tabWidth: 4,
    paneWidth: 12
  },
  // only applicable to vertical menus.
  menuPosition: 'left',
  // only applicable to vertical menus.
  fullWidthVertical: false,
  onTabChange: function onTabChange() {
    return null;
  },
  onHideActiveTab: function onHideActiveTab() {
    return null;
  },
  className: ''
};
export function getFinalPanes() {
  var panes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var children = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    _ref$menuItemProps = _ref.menuItemProps,
    menuItemProps = _ref$menuItemProps === void 0 ? {} : _ref$menuItemProps;
  var finalPanes = [];
  if (!_isEmpty(panes)) {
    finalPanes = panes;
  } else if (!_isEmpty(children)) {
    var items = findComponentsByType(children, 'Item');
    finalPanes = items.map(function (item) {
      var itemChildren = _get(item, 'props.children', []);
      var menuItem = findComponentsByType(itemChildren, 'MenuItem')[0];
      var content = findComponentsByType(itemChildren, 'Content')[0];
      if (_isNil(menuItem) || _isNil(content)) {
        return null;
      }
      return {
        menuItem: menuItem,
        render: function render() {
          return content;
        }
      };
    }).filter(Boolean);
  }
  return finalPanes.map(function (pane) {
    var _ref2 = pane || {},
      menuItem = _ref2.menuItem,
      rest = _objectWithoutProperties(_ref2, _excluded);
    if (_isString(menuItem)) {
      return _objectSpread(_objectSpread({}, rest), {}, {
        menuItem: createComponent(Tabs.MenuItem, _objectSpread({
          children: menuItem
        }, menuItemProps))
      });
    }
    return pane;
  });
}
export default function Tabs(_ref3) {
  var activeIndexProp = _ref3.activeIndex,
    defaultActiveIndex = _ref3.defaultActiveIndex,
    deselectable = _ref3.deselectable,
    panesProp = _ref3.panes,
    children = _ref3.children,
    vertical = _ref3.vertical,
    menuProps = _ref3.menuProps,
    menuPosition = _ref3.menuPosition,
    fullWidthVertical = _ref3.fullWidthVertical,
    grid = _ref3.grid,
    onTabChangeProp = _ref3.onTabChange,
    onHideActiveTabProp = _ref3.onHideActiveTab,
    className = _ref3.className;
  var _useEventPropagation = useEventPropagation(),
    preventPropagation = _useEventPropagation.preventPropagation;
  var _useState = useState(defaultActiveIndex),
    _useState2 = _slicedToArray(_useState, 2),
    activeIndex = _useState2[0],
    setActiveIndex = _useState2[1];
  var isControlled = activeIndexProp !== undefined;
  function onTabChange(e, data) {
    var newActiveIndex = data.activeIndex;
    if (!isControlled) {
      setActiveIndex(newActiveIndex);
    }
    if (onTabChangeProp) {
      onTabChangeProp(e, data);
    }
  }
  function hideActiveTab(e) {
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!isControlled && deselectable) {
      setActiveIndex(null);
    }
    if (onHideActiveTabProp) {
      onHideActiveTabProp(e, data);
    }
    if (e) {
      preventPropagation(e, {
        preventDefault: true
      });
    }
  }
  var finalPanes = getFinalPanes(panesProp, children);
  var finalMenuProps = getMenuProps(vertical, menuProps);
  var finalActiveIndex = isControlled ? activeIndexProp : activeIndex;
  var finalGridProps = getGridProps(grid, vertical, fullWidthVertical, finalActiveIndex);
  var finalClassName = classNames(_defineProperty({
    'foundation-tabs': true
  }, className, true));
  var contextValue = {
    isControlled: isControlled,
    deselectable: deselectable,
    hideActiveTab: hideActiveTab,
    activeIndex: activeIndex
  };
  if (_isEmpty(finalPanes)) {
    return null;
  }
  return /*#__PURE__*/React.createElement(TabsContextProvider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(SemanticUiTab, {
    className: finalClassName,
    activeIndex: finalActiveIndex,
    panes: finalPanes,
    menu: finalMenuProps,
    grid: finalGridProps,
    menuPosition: menuPosition,
    onTabChange: onTabChange
  }));
}
Tabs.defaultProps = defaultProps;
Tabs.Item = Item;
Tabs.Content = Content;
Tabs.MenuItem = MenuItem;